import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_CUSTOMERS,
  MN_ORDER_COUNTERS,
  MODULE,
} from '../common';
import type { Prisma } from '@prisma/client';
import * as z from 'zod';
import { util } from 'zod';
import type { KeyValue } from '@jobcost-monorepo/utils';
import Omit = util.Omit;

type OrderCounter = Prisma.$OrderCounterPayload['scalars'];

export type IOrderCounter = OrderCounter &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'> & {
    customer?: KeyValue;
  };

export interface IListOrderCounterFetchParams extends IListFetchParams {
  customerId?: number;
  year?: number;
}

class OrderCounterRepository extends BaseRepository<
  IOrderCounter,
  IListOrderCounterFetchParams
> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['code', 'counter', 'year'],
    };
    this.gridFieldsOptions = [
      {
        visible: true,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'code',
        type: 'string',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'counter',
        type: 'number',
        align: 'right',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 3,
        linkedField: 'year',
        type: 'number',
        align: 'right',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 4,
        linkedField: 'customer.name',
        type: 'string',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'customerId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_CUSTOMERS,
        translationModuleName: MN_ORDER_COUNTERS,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_ORDER_COUNTERS,
        translationModuleName: MN_ORDER_COUNTERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 0 },
        linkedField: 'customerId',
        linkedFieldForDefaultValue: 'customer.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_CUSTOMERS,
        translationModuleName: MN_ORDER_COUNTERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'code',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_ORDER_COUNTERS,
        translationModuleName: MN_ORDER_COUNTERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'year',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_ORDER_COUNTERS,
        translationModuleName: MN_ORDER_COUNTERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 1 },
        linkedField: 'counter',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_ORDER_COUNTERS,
        translationModuleName: MN_ORDER_COUNTERS,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      customerId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      year: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      counter: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      code: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(10, { message: 'max_10' }),
    });
  }
}

export default OrderCounterRepository;
